package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.Span

/**
 * Inline-SVG icon set replacing the functional emoji glyphs. Emoji render from
 * the platform's color-emoji font, so they ignore CSS `color` (the .btn.icon
 * grey/hover/danger states) and draw differently per OS. These are hand-drawn
 * Feather-style strokes on a 24x24 grid with stroke="currentColor", so they
 * inherit whatever color the surrounding control sets. The masthead 📁 is
 * deliberately NOT one of these — that's branding, pending the real logo.
 */
enum class IconName { FOLDER, FILE, SEARCH, DOWNLOAD, TRASH, CLOSE, REFRESH, EXTRACT, PERSON, TEAM, SHARE, LEAVE, NEW_FOLDER, BACK, CHEVRON }

private fun svg(body: String): String =
    "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\" " +
        "stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\">$body</svg>"

private val ICON_SVGS: Map<IconName, String> = mapOf(
    IconName.FOLDER to svg("<path d=\"M3 7a2 2 0 0 1 2-2h4.6l2 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z\"/>"),
    IconName.FILE to svg("<path d=\"M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z\"/><polyline points=\"14 3 14 7 18 7\"/>"),
    IconName.SEARCH to svg("<circle cx=\"11\" cy=\"11\" r=\"7\"/><line x1=\"16.5\" y1=\"16.5\" x2=\"20.5\" y2=\"20.5\"/>"),
    IconName.DOWNLOAD to svg("<path d=\"M12 4v11\"/><polyline points=\"7 10.5 12 15.5 17 10.5\"/><path d=\"M4 20h16\"/>"),
    IconName.TRASH to svg("<polyline points=\"4 7 20 7\"/><path d=\"M6 7l1 13h10l1-13\"/><path d=\"M9 7V4h6v3\"/><line x1=\"10\" y1=\"11\" x2=\"10\" y2=\"17\"/><line x1=\"14\" y1=\"11\" x2=\"14\" y2=\"17\"/>"),
    IconName.CLOSE to svg("<line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"/><line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"/>"),
    IconName.REFRESH to svg("<path d=\"M20 12a8 8 0 1 1-2.34-5.66\"/><polyline points=\"17.66 1.9 17.66 6.34 22 6.34\"/>"),
    // Extract: FOLDER's shape, shifted down to leave room for an arrow escaping
    // upward from it — "contents coming OUT of the archive into a new folder".
    IconName.EXTRACT to svg(
        "<path d=\"M3 10a2 2 0 0 1 2-2h4.6l2 2H19a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z\"/>" +
            "<path d=\"M12 8V3\"/><polyline points=\"9 6 12 3 15 6\"/>",
    ),
    // ADV-1330: the share picker marks each row's kind (S5), and the row action opens the dialog.
    IconName.PERSON to svg("<circle cx=\"12\" cy=\"8\" r=\"4\"/><path d=\"M4 21c0-4 3.6-7 8-7s8 3 8 7\"/>"),
    IconName.TEAM to svg(
        "<circle cx=\"9\" cy=\"8\" r=\"3.5\"/><path d=\"M2 20c0-3.5 3-6 7-6s7 2.5 7 6\"/>" +
            "<path d=\"M16 4.5a3.5 3.5 0 0 1 0 7\"/><path d=\"M18 14c2.5.6 4 2.9 4 6\"/>",
    ),
    // Leave: a door with an arrow walking out of it.
    IconName.LEAVE to svg(
        "<path d=\"M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4\"/>" +
            "<polyline points=\"9 8 5 12 9 16\"/><line x1=\"5\" y1=\"12\" x2=\"15\" y2=\"12\"/>",
    ),
    IconName.SHARE to svg(
        "<circle cx=\"9\" cy=\"8\" r=\"4\"/><path d=\"M2 21c0-4 3-7 7-7s7 3 7 7\"/>" +
            "<line x1=\"19\" y1=\"8\" x2=\"19\" y2=\"14\"/><line x1=\"16\" y1=\"11\" x2=\"22\" y2=\"11\"/>",
    ),
    // ADV-1529, the extract dialog's folder browser: FOLDER with a plus, a back arrow, a chevron.
    IconName.NEW_FOLDER to svg(
        "<path d=\"M3 7a2 2 0 0 1 2-2h4.6l2 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z\"/>" +
            "<line x1=\"12\" y1=\"10.5\" x2=\"12\" y2=\"16.5\"/><line x1=\"9\" y1=\"13.5\" x2=\"15\" y2=\"13.5\"/>",
    ),
    IconName.BACK to svg("<line x1=\"19\" y1=\"12\" x2=\"5\" y2=\"12\"/><polyline points=\"11 6 5 12 11 18\"/>"),
    IconName.CHEVRON to svg("<polyline points=\"9 6 15 12 9 18\"/>"),
)

/** Static markup from the map above — never user input — so innerHTML is safe here. */
@Composable
fun Icon(name: IconName) {
    Span(attrs = {
        classes("icon-svg")
        ref { el ->
            el.innerHTML = ICON_SVGS.getValue(name)
            onDispose { el.innerHTML = "" }
        }
    })
}
